import { FC, useState, useCallback } from 'react'
import ShopFilters from '@/components/features/ShopFilters'
import ProductGrid from '@/components/features/ProductGrid'
import { getSeoContent } from '@/content/siteContent'
import { useI18n } from '@/hooks'
import { usePageMetadata } from '@/seo/usePageMetadata'

const showShopFilters = false

interface ShopPageProps {
  onSearch?: (query: string) => void
  className?: string
}

const ShopPage: FC<ShopPageProps> = ({ 
  onSearch,
  className = '' 
}) => {
  const { lang, fontClass, locale } = useI18n()
  const [priceRange, setPriceRange] = useState<{ min: number; max: number } | undefined>()
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedFormats, setSelectedFormats] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const seo = getSeoContent(lang).shop

  usePageMetadata({
    title: seo.title,
    description: seo.description,
    path: '/shop',
    locale,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: seo.title,
      description: seo.description,
      url: `${window.location.origin}/shop`,
    },
  })

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    if (onSearch) {
      onSearch(query)
    } else {
      console.log('Searching for:', query)
    }
  }, [onSearch])

  const handlePriceFilter = useCallback((min: number, max: number) => {
    setPriceRange({ min, max })
  }, [])

  const handleCategoryFilter = useCallback((categories: string[]) => {
    setSelectedCategories(categories)
  }, [])

  const handleFormatFilter = useCallback((formats: string[]) => {
    setSelectedFormats(formats)
  }, [])

  return (
    <main className={`min-h-screen bg-white pt-14 ${fontClass} ${className}`}>
      <h1 className="sr-only">{lang === 'zh' ? 'CloutySpace 商品總覽' : 'CloutySpace Shop'}</h1>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          {showShopFilters && (
            <div className="lg:w-1/4">
              <div className="bg-white">
                <ShopFilters
                  lang={lang}
                  onPriceFilter={handlePriceFilter}
                  onCategoryFilter={handleCategoryFilter}
                  onFormatFilter={handleFormatFilter}
                />
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className={showShopFilters ? 'lg:w-3/4' : 'w-full'}>
            <ProductGrid
              lang={lang}
              priceRange={priceRange}
              selectedCategories={selectedCategories}
              selectedFormats={selectedFormats}
              searchQuery={searchQuery}
              onSearch={handleSearch}
            />
          </div>
        </div>
      </div>
    </main>
  )
}

export default ShopPage
